import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { SscRequest } from './ssc-request.model';
import { SscResponse } from './ssc-response.model';

/**
 * An SscInterceptor intercepts calls to the ssc backend by
 *  * modifying or replacing the SscRequest to send to the backend,
 *  * execute (or suppress) the call down the chain to the ssc backend.
 *  * modifying or replacing the SscResponse sent back _from_ the backend,
 */
export abstract class SscInterceptor {

    /**
     * override this, if you want to pimp the request and return the changed (or exchanged) request.
     *
     * Keep in mind, that the returned object might (and sometimes should) be a different object than
     * the one passed as parameter, so changes to the request are visible to the following interceptors
     * and the backend, but not to the caller.  The original Object should not be changed when send to the
     * ssc backend.
     */
    public configure(sscRequest: SscRequest, _sscCallContext: ISscCallContext): SscRequest {
        return sscRequest;
    }

    /**
     * override this if you want to intercept the call to the ssc (and possibly suppress the call to the
     * ssc).
     */
    public intercept(sscRequest: SscRequest, sscInterceptorChain: ISscInterceptorChain, sscCallContext: ISscCallContext): Observable<SscResponse> {
        const pimpedSscRequest: SscRequest = this.configure(sscRequest, sscCallContext);
        const ret: Observable<SscResponse> = sscInterceptorChain.doIntercept(pimpedSscRequest, sscCallContext);
        return this.postProcess(ret, sscCallContext);
    }

    /**
     * override this, if you want to pimp the response and return the changed (or exchanged) response.
     */
    public postProcess(sscResponse: Observable<SscResponse>, _sscCallContext: ISscCallContext): Observable<SscResponse> {
        return sscResponse;
    }
}

/**
 * interface for a parameter object that is propagated to all interceptors during a call and the ssc backend (caller).
 *
 * e.g.:
 * there might be general activated interceptors, that converts "<null>" strings to null strings and your want
 * that interceptor to be active for THAT call or use-case.  Add the property "make_nulls_great_again": true
 * to the context and implements the Interceptor that it works only,if it finds this prop in this context.
 */
/**
 * @deprecated use ISscCallContext instead, will be removed in future version
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface,@typescript-eslint/naming-convention
export interface SscCallContext {
}

/**
 * interface for a parameter object that is propagated to all interceptors during a call and the ssc backend (caller).
 *
 * e.g.:
 * there might be general activated interceptors, that converts "<null>" strings to null strings and your want
 * that interceptor to be active for THAT call or use-case.  Add the property "make_nulls_great_again": true
 * to the context and implements the Interceptor that it works only,if it finds this prop in this context.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ISscCallContext {
}

/**
 * @deprecated use ISscInterceptorChain instead, will be removed in future version
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface SscInterceptorChain {
    /**
     * delegated the call to the next interceptor or (finally) the ssc backend.
     * @param sscRequest
     * @param sscCallContext
     */
    doIntercept(sscRequest: SscRequest, sscCallContext: ISscCallContext): Observable<SscResponse>;
}

export interface ISscInterceptorChain {
    /**
     * delegated the call to the next interceptor or (finally) the ssc backend.
     * @param sscRequest
     * @param sscCallContext
     */
    doIntercept(sscRequest: SscRequest, sscCallContext: ISscCallContext): Observable<SscResponse>;
}

export const SSC_INTERCEPTORS: InjectionToken<SscInterceptor[]> = new InjectionToken('SSC_INTERCEPTORS');
